import React, { useCallback, useMemo } from 'react';
import classnames from 'classnames';

import { useAppDispatch, useAppSelector } from '../../../hooks/react-redux';
import { resetUserDefaults, saveUserDefaults, updateUserDefaults } from '../../home/store/actions';
import { getCurrentUserDefaults, getUserDefaultsUpdated } from '../../home/store/selectors';
import { Settings } from '../../shared/icons';
import { ConfirmationModal } from '../../shared/modal/ConfirmationModal';
import { ModalHeader } from '../../shared/modal/ModalHeader';
import styles from './Documents.module.scss';
import { getColumnConfigurationModalOpen, toggleDocumentConfigurationModal } from './store';
import { DraggableItem } from '../../shared/drag-n-drop/shared';
import { allAvailableDocumentColumns } from '../../constants/documents';
import { DragDrop } from '../../shared/drag-n-drop/DragDrop';
import { Toggle } from '../../shared/toggle';
import { InformationTooltip } from '../../shared/tooltip';
import { Scrollable } from '../../shared/scrollable/Scrollable';

const { amber, green, white } = styles;

// In multiple places in this file we force the actions column to always be the last column
const ACTIONS_COLUMN = 'actions';

interface DocumentColumnProps {
    id: string;
    label: string;
    description: string;
    index: number;
    toggleColumn: (checked: boolean, id: string) => void;
    isDragging?: boolean;
    isFirstColumn: boolean;
    isLastColumn: boolean;
    includedColumn: boolean;
}

const DocumentColumn: React.FC<DocumentColumnProps> = ({ id, label, description, isDragging = false, toggleColumn, isFirstColumn, isLastColumn, includedColumn }) => (
    <div className={classnames(styles.columnWrapper, {
        [styles.firstColumnBorder]: isFirstColumn,
        [styles.lastColumnBorder]: isLastColumn
    })}>
        <div className={classnames(styles.headerWrapper, {
            [styles.draggingColumn]: isDragging,
            [styles.firstColumnBorder]: isFirstColumn,
            [styles.lastColumnBorder]: isLastColumn
        })}>
            <div className={styles.headerLabel}>{label}</div>
            <InformationTooltip content={description} labelColor={white} />
            {!isDragging && id !== ACTIONS_COLUMN && <Toggle checked={includedColumn} onChange={checked => toggleColumn(checked, id)} onColour={green} />}
        </div>
        <div className={classnames(styles.firstTableCellWrapper, {
            [styles.firstCellBorder]: isFirstColumn,
            [styles.lastCellBorder]: isLastColumn
        })} />
        <div className={classnames(styles.secondTableCellWrapper, {
            [styles.firstCellBorder]: isFirstColumn,
            [styles.lastCellBorder]: isLastColumn
        })} />
    </div>
);

export const ColumnConfigurationModal: React.FC = () => {
    const dispatch = useAppDispatch();
    const isOpen = useAppSelector(getColumnConfigurationModalOpen);
    const { documentColumns } = useAppSelector(getCurrentUserDefaults);
    const hasUpdated = useAppSelector(getUserDefaultsUpdated);

    const includedColumnOptions = useMemo(() => documentColumns.map(columnId => {
        const { id, label } = allAvailableDocumentColumns.find(({ id }) => id === columnId)!;
        return { id, label };
    }), [documentColumns]);

    const closeModal = useCallback(() => {
        dispatch(resetUserDefaults());
        dispatch(toggleDocumentConfigurationModal(false));
    }, [dispatch]);

    const updateDocumentColumns = useCallback((value: string[]) => dispatch(updateUserDefaults('documentColumns', value)), [dispatch]);

    const saveColumnConfiguration = useCallback(() => dispatch(saveUserDefaults()), [dispatch]);

    const updateList = useCallback((list: DraggableItem[]) => {
        const newColumns = [...list.filter(({ id }) => id !== ACTIONS_COLUMN).map(({ id }) => id), ACTIONS_COLUMN];
        updateDocumentColumns(newColumns);
    }, [updateDocumentColumns]);

    const toggleColumn = useCallback((checked: boolean, id: string) => {
        const updatedList = checked ? [...documentColumns.filter(columnId => columnId !== ACTIONS_COLUMN), id, ACTIONS_COLUMN] : [...documentColumns.filter(columnId => ![ACTIONS_COLUMN, id].includes(columnId)), ACTIONS_COLUMN];
        updateDocumentColumns(updatedList);
    }, [documentColumns, updateDocumentColumns]);

    const getChildElement = useCallback((childId: string, index: number) => {
        const column = allAvailableDocumentColumns.find(({ id }) => id === childId);
        const isLastColumn = index === includedColumnOptions.length - 1;
        const isFirstColumn = index === 0;
        if (column) {
            const { id, label, description } = column;
            return (
                <DocumentColumn
                    id={id}
                    label={label}
                    index={index}
                    description={description}
                    toggleColumn={toggleColumn}
                    isFirstColumn={isFirstColumn}
                    isLastColumn={isLastColumn}
                    includedColumn
                />
            );
        }
        return null;
    }, [toggleColumn, includedColumnOptions]);

    const getDraggingElement = useCallback((childId: string, index: number) => {
        const column = allAvailableDocumentColumns.find(({ id }) => id === childId)!;
        const { id, label, description } = column;
        const isLastColumn = index === includedColumnOptions.length - 1;
        const isFirstColumn = index === 0;
        return (
            <DocumentColumn
                id={id}
                label={label}
                index={index}
                description={description}
                isDragging
                toggleColumn={toggleColumn}
                isFirstColumn={isFirstColumn}
                isLastColumn={isLastColumn}
                includedColumn
            />
        );
    }, [toggleColumn, includedColumnOptions]);

    const otherColumns = useMemo(() => allAvailableDocumentColumns.filter(({ id }) => !documentColumns.includes(id)), [documentColumns]);

    return (
        <ConfirmationModal
            isOpen={isOpen}
            closeModal={closeModal}
            closeLabel='Close'
            confirmLabel='Save'
            confirmDisabled={!hasUpdated}
            testId='documents-column-configuration'
            confirm={saveColumnConfiguration}
        >
            <div className={styles.columnConfigurationWrapper} data-testid='documents-column-configuration'>
                <ModalHeader label='Configure Columns' icon={Settings} />
                <div className={styles.columnConfigurationBody}>
                    <div className={styles.columnConfigurationHeader}>Configure which of the available columns you would like to include in your Documents table and the order they are displayed</div>
                    <div className={styles.columnConfigurationLabel}>Active Columns</div>
                    <div className={styles.currentColumns}>
                        <DragDrop
                            getChildElement={getChildElement}
                            list={includedColumnOptions}
                            listId='document-reporting'
                            updateList={updateList}
                            type='horizontal'
                            getDraggingElement={getDraggingElement}
                            edgeColour={amber}
                            edgeWidth={3}
                        />
                    </div>
                    <div className={styles.columnConfigurationLabel}>Other Available Columns</div>
                    <div className={styles.additionalColumnOptions}>
                        <Scrollable>
                            <div className={styles.otherColumnsWrapper}>
                                {otherColumns.map(({ id, label, description }, index) => {
                                    const isLastColumn = index === otherColumns.length - 1;
                                    const isFirstColumn = index === 0;
                                    return (
                                        <DocumentColumn
                                            key={id}
                                            id={id}
                                            label={label}
                                            index={index}
                                            description={description}
                                            toggleColumn={toggleColumn}
                                            isFirstColumn={isFirstColumn}
                                            isLastColumn={isLastColumn}
                                            includedColumn={false}
                                        />
                                    );
                                })}
                            </div>
                        </Scrollable>
                    </div>
                </div>
            </div>
        </ConfirmationModal>
    );
};
